/**
 * Quote List Component
 * Manages a list of quote options with add/edit/remove functionality
 */

import { Plus, Trash2, Plane } from 'lucide-react';
import type { Quote } from '../types';
import { MileageProgram, MILEAGE_PROGRAM_LABELS } from '../types';
import { calculateLegCost, formatCurrency } from '../utils/calculations';

interface QuoteListProps {
  title: string;
  quotes: Quote[];
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<Quote>) => void;
  onRemove: (id: string) => void;
  icon?: 'outbound' | 'return';
  canRemove?: boolean;
}

export function QuoteList({
  title,
  quotes,
  onAdd,
  onUpdate,
  onRemove,
  icon = 'outbound',
  canRemove = true,
}: QuoteListProps) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            icon === 'outbound'
              ? 'bg-[#B05D41]/10 text-[#B05D41]'
              : 'bg-blue-500/10 text-blue-500'
          }`}>
            <Plane className={`w-5 h-5 ${icon === 'return' ? 'rotate-180' : ''}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
            <p className="text-sm text-zinc-400">
              {quotes.length} {quotes.length === 1 ? 'opção' : 'opções'}
            </p>
          </div>
        </div>

        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 bg-[#B05D41] hover:bg-[#8B4A33] text-white rounded-lg font-medium transition-all"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Adicionar Cotação</span>
        </button>
      </div>

      {/* Quote Cards */}
      <div className="space-y-3">
        {quotes.map((quote, index) => (
          <QuoteCard
            key={quote.id}
            quote={quote}
            index={index}
            onUpdate={(updates) => onUpdate(quote.id, updates)}
            onRemove={() => onRemove(quote.id)}
            canRemove={canRemove && quotes.length > 1}
          />
        ))}
      </div>
    </div>
  );
}

interface QuoteCardProps {
  quote: Quote;
  index: number;
  onUpdate: (updates: Partial<Quote>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

// Compact Currency Input Component for Grid Layout
function CompactCurrencyInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-zinc-300">
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
          R$
        </span>
        <input
          type="text"
          value={value > 0
            ? value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })
            : ''}
          onChange={(e) => {
            const numValue = e.target.value.replace(/[^\d]/g, '');
            onChange(numValue ? parseFloat(numValue) / 100 : 0);
          }}
          placeholder="0,00"
          className="w-full pl-10 pr-3 py-2 text-sm bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#B05D41]/50 focus:border-[#B05D41]/50 transition-all"
        />
      </div>
    </div>
  );
}

function QuoteCard({ quote, index, onUpdate, onRemove, canRemove }: QuoteCardProps) {
  const cost = calculateLegCost(quote);

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4 space-y-4 hover:border-zinc-700/50 transition-all">
      {/* Header with Remove Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-semibold text-zinc-300">
            {index + 1}
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-300">
              Opção {index + 1}
            </p>
            <p className="text-xs text-zinc-500">
              Custo: <span className="text-zinc-400">{formatCurrency(cost)}</span>
            </p>
          </div>
        </div>

        {canRemove && (
          <button
            onClick={onRemove}
            className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
            title="Remover esta opção"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Form Fields - Compact Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Mileage Program */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-zinc-300">
            Programa
          </label>
          <select
            value={quote.program}
            onChange={(e) => onUpdate({ program: e.target.value as MileageProgram })}
            className="w-full px-3 py-2 text-sm bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#B05D41]/50 focus:border-[#B05D41]/50 transition-all"
          >
            {Object.entries(MILEAGE_PROGRAM_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Miles - Compact */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-zinc-300">
            Milhas
          </label>
          <input
            type="text"
            value={quote.miles > 0 ? quote.miles.toLocaleString('pt-BR') : ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              onUpdate({ miles: value ? parseInt(value, 10) : 0 });
            }}
            placeholder="0"
            className="w-full px-3 py-2 text-sm bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#B05D41]/50 focus:border-[#B05D41]/50 transition-all"
          />
        </div>

        {/* Cost per Thousand - Compact */}
        <CompactCurrencyInput
          label="Custo Milheiro"
          value={quote.costPerThousand}
          onChange={(value) => onUpdate({ costPerThousand: value })}
        />

        {/* Boarding Tax - Compact */}
        <CompactCurrencyInput
          label="Taxa Embarque"
          value={quote.boardingTax}
          onChange={(value) => onUpdate({ boardingTax: value })}
        />
      </div>
    </div>
  );
}
